import { Add, AutoAwesome, Send } from '@mui/icons-material';
import { Box, Chip, IconButton, TextField } from '@mui/material';
import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import './AiAssistant.css';
import { getLatestAiSession } from '../../data/AiAssistant';
import { v4 as uuidv4 } from 'uuid';
import { useNamedWebSocket } from '../../data/websocket';
import UserContext from '../../UserContext';
import { FormattedMessage, useIntl } from 'react-intl';
import ReactMarkdown from 'react-markdown';

const AiAssistant = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [conversationId, setConversationId] = useState('');
  /* eslint-disable-next-line */
  const [mode, setMode] = useState('INTELLIGENT');
  const [connectWebsocket, setConnectWebsocket] = useState(false);
  const context = useContext(UserContext);

  const intl = useIntl();
  const boxContainerRef = useRef(null);
  const receiveMessage = useCallback((message) => {
    const payload = JSON.parse(message.data);

    if (payload.isAnswer) {
      setMessages(prevMessages => {
        const idx = prevMessages.findIndex(m => m.id === payload.messageId);

        if (idx === -1) {
          return [
            ...prevMessages.filter(item => !(item.id === 'toDelete' || item.id.startsWith('toDelete'))),
            {
              id: payload.messageId,
              text: payload.response || '',
              owner: 'bot',
              scrolling: false
            }
          ];
        }

        let newMessages = [...prevMessages];
        const old = newMessages[idx];
        newMessages = newMessages.filter(item => !(item.id === 'toDelete' || item.id.startsWith('toDelete')));

        newMessages[idx] = {
          ...old,
          text: (old.text || '') + (payload.response || ''),
          scrolling: false
        };

        console.log(payload);
        setConversationId(payload.conversationId);
        return newMessages;
      });
    } else {
      setMessages(prevMessages => [
        ...prevMessages.filter(m => !(m.id === 'toDelete' + payload.messageId || m.id === 'toDelete')),
        { text: payload.response, owner: 'bot', id: 'toDelete' + payload.messageId, scrolling: false, reasoning: true }
      ]);
    }
  }, []);
  /* eslint-disable-next-line */
  const { sendJsonMessage, lastMessage, readyState, disconnect, reconnect } = useNamedWebSocket('AiAssistentAPI', {
    enabled: connectWebsocket,
    onMessage: receiveMessage
  });

  const scrollToBottom = () => {
    if (boxContainerRef.current) {
      boxContainerRef.current.scrollTo({
        top: boxContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    if (!connectWebsocket && document.body.classList?.contains('openAiAssistant')) {
      setConnectWebsocket(true);
    }
  });

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const initializeSession = async () => {
      try {
        const response = await getLatestAiSession();
        if (response?.conversationId) {
          setConversationId(response.conversationId);
          setMessages(response.messages.map(element => {
            if (element.messageType === 'ASSISTANT') {
              return { text: element.text, owner: 'bot', id: 'oldMessage' + uuidv4() };
            } else {
              return { text: element.text, owner: 'user', id: 'oldMessage' + uuidv4() };
            }
          }));
        }
      } catch (error) {
        if (error.response?.status !== 404) {
          console.error(error);
        }
      }
    };

    initializeSession();
    return () => {
      disconnect();
    };
  }, []);

  const publishMessage = async (inp) => {
    if (!inp?.trim()) return;
    sendJsonMessage({
      MessageGroupId: context.user.signInUserSession.idToken.payload.sub,
      path: '/conversation/quest',
      body: { message: inp, url: window.location.href, conversationId: conversationId, mode }
    });
    setInput('');
    setMessages(prevMessages => [
      ...prevMessages,
      { text: inp, owner: 'user', id: inp + Date.now(), scrolling: true },
      { text: intl?.formatMessage({ id: 'aiAssistant.publishMessage.successMessage' }), owner: 'bot', id: 'toDelete', scrolling: true, reasoning: true }
    ]);
  };

  const handleNewSession = (e) => {
    e.preventDefault();
    // setMessages([{ text: intl?.formatMessage({ id: 'aiAssistant.newSession.successMessage' }), owner: 'bot', id: 'toDelete' + Date.now() }]);
    setMessages([]);
    setConversationId('');
  };

  return (
    <div className='aiAssistant'>

      {/* Show info before first search */}
      <Box className='chat-messages' ref={boxContainerRef}>
        {(!messages || messages.length === 0) &&
        <div className="getStarted">
          <div className="imageContainer">
            <img src='/img/aiAssistant/undraw_things-to-say_f5mi.svg' alt="Ai Assistant"/>
          </div>
          <h2><FormattedMessage id='aiAssistant.getStarted.header' /></h2>
          <p><FormattedMessage id='aiAssistant.getStarted.description' /></p>

          <div className="examples">
            <Chip icon={<AutoAwesome />} label={intl?.formatMessage({ id: 'aiAssistant.example.1' })} onClick={() => publishMessage(intl?.formatMessage({ id: 'aiAssistant.example.1' }))}/>
            <Chip icon={<AutoAwesome />} label={intl?.formatMessage({ id: 'aiAssistant.example.2' })} onClick={() => publishMessage(intl?.formatMessage({ id: 'aiAssistant.example.2' }))}/>
            <Chip icon={<AutoAwesome />} label={intl?.formatMessage({ id: 'aiAssistant.example.3' })} onClick={() => publishMessage(intl?.formatMessage({ id: 'aiAssistant.example.3' }))}/>
            <Chip icon={<AutoAwesome />} label={intl?.formatMessage({ id: 'aiAssistant.example.4' })} onClick={() => publishMessage(intl?.formatMessage({ id: 'aiAssistant.example.4' }))}/>
          </div>
        </div>
        }

        {/* Message Area */}
        {messages?.map((msg, idx) => (
          <div
            key={msg.id}
            className={`message ${msg.owner === 'user' ? 'user' : 'bot'} ${msg?.reasoning ? 'reasoning' : ''}`}
          >
            {msg?.reasoning
              ? <div dangerouslySetInnerHTML={{ __html: msg.text }}/>
              : <ReactMarkdown>
                {msg.text}
              </ReactMarkdown>
            }
          </div>
        ))}
      </Box>

      {/* Input Area */}
      <form onSubmit={async (e) => { e.preventDefault(); await publishMessage(input); }}>
        <TextField
          id='aiAssistantInput'
          multiline
          minRows={2}
          maxRows={10}
          fullWidth
          autoFocus
          placeholder={intl?.formatMessage({ id: 'aiAssistant.input.placeholder' }) }
          variant="outlined"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyUp={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              publishMessage(input);
            }
          }}
        />
        <div className='toolbar'>
          <IconButton
            onClick={(e) => handleNewSession(e)}
            size='small'
            aria-label={intl.formatMessage({ id: 'aiAssistant.newSession' })}
          >
            <Add />
          </IconButton>
          <IconButton
            type="submit"
            size='small'
            disabled={!input.trim()}
            aria-label={intl.formatMessage({ id: 'aiAssistant.submitSearch' })}
          >
            <Send />
          </IconButton>
        </div>
      </form>
    </div>
  );
};

export default AiAssistant;
